import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Doughnut } from 'react-chartjs-2';

const ExtendedTooltipOverlay = styled.div`
  position: fixed;
  top: ${props => props.top}px;
  left: ${props => props.left}px;
  background: linear-gradient(135deg, rgba(44, 62, 80, 0.95), rgba(52, 73, 94, 0.95));
  color: white;
  padding: 25px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  z-index: 10000;
  backdrop-filter: blur(10px);
  max-width: 700px;
  width: 700px;
  min-height: 400px;
  overflow: visible;
  pointer-events: auto;
  animation: fadeInScale 0.3s ease-out;
  
  @keyframes fadeInScale {
    0% {
      opacity: 0;
      transform: scale(0.8);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  /* Only apply responsive behavior on very small screens */
  @media (max-width: 480px) {
    max-width: 95vw;
    width: 95vw;
    left: 2.5vw !important;
    padding: 20px;
  }
  
  /* For tablets, still use full width */
  @media (min-width: 481px) and (max-width: 800px) {
    width: 500px;
    max-width: 500px;
  }
`;

const GeographicSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 30px;
  width: 100%;
  
  /* Only stack vertically on very small screens */
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const PieChartContainer = styled.div`
  width: 200px;
  height: 200px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50%;
  padding: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 480px) {
    width: 150px;
    height: 150px;
    margin: 0 auto;
  }
`;

const CountryList = styled.div`
  flex: 1;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 8px;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: 480px) {
    max-height: 160px;
    padding-right: 4px;
    width: 100%;
  }
`;

const TooltipHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  padding-bottom: 12px;
`;

const TooltipTitle = styled.h4`
  margin: 0;
  font-size: 1.2rem;
  color: #fff;
  font-weight: 600;
  flex: 1;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const TooltipSection = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TooltipLabel = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:before {
    content: '';
    width: 4px;
    height: 16px;
    background: linear-gradient(45deg, #4FC3F7, #29B6F6);
    border-radius: 2px;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin: 12px 0;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const SummaryItem = styled.div`
  background: rgba(255, 255, 255, 0.08);
  padding: 12px 16px;
  border-radius: 8px;
  border-left: 3px solid ${props => props.color || '#4FC3F7'};
  
  .label {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 4px;
  }
  
  .value {
    font-size: 1.1rem;
    font-weight: 600;
    color: white;
  }
`;

const CountryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  margin-bottom: 6px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.95);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border-left: 3px solid ${props => props.flagColor};
  transition: background-color 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .country-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .country-stats {
    text-align: right;
    font-weight: 500;
  }
`;

const CountryFlag = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: ${props => props.color};
  display: inline-block;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;



const TooltipOverlay = ({ tooltipData, position, onClose }) => {
  if (!tooltipData) return null;

  // Format date with timezone
  const formatDateWithTimezone = (dateString) => {
    const date = new Date(dateString);
    const options = {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZoneName: 'short'
    };
    return date.toLocaleDateString('en-US', options);
  };

  // Handle close button click (prevent event bubbling to overlay)
  const handleCloseClick = (e) => {
    e.stopPropagation();
    onClose();
  };

  // Handle overlay click but allow content interaction
  const handleOverlayClick = (e) => {
    // Only close if clicking the overlay background, not the content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ExtendedTooltipOverlay 
      top={position.top} 
      left={position.left}
      onClick={handleOverlayClick}
      data-tooltip-overlay="true"
    >
      <TooltipHeader>
        <TooltipTitle>
          {formatDateWithTimezone(tooltipData.date)}
        </TooltipTitle>
        <CloseButton onClick={handleCloseClick}>
          ✕
        </CloseButton>
      </TooltipHeader>
      
      <SummaryGrid>
        <SummaryItem color="#4FC3F7">
          <div className="label">Sessions</div>
          <div className="value">{tooltipData.sessionCount.toLocaleString()}</div>
        </SummaryItem>
        <SummaryItem color="#66BB6A">
          <div className="label">Total Time</div>
          <div className="value">{(tooltipData.totalTimePlayed / 3600).toFixed(1)}h</div>
        </SummaryItem>
      </SummaryGrid>
      
      {tooltipData.countryData && tooltipData.countryData.chartData && (
        <TooltipSection>
          <TooltipLabel>Geographic Distribution</TooltipLabel>
          <GeographicSection>
            <PieChartContainer>
              <Doughnut 
                data={tooltipData.countryData.chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: '50%',
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      enabled: false
                    }
                  },
                  elements: {
                    arc: {
                      borderWidth: 2,
                      borderColor: 'rgba(255, 255, 255, 0.8)'
                    }
                  }
                }}
              />
            </PieChartContainer>
            
            <CountryList>
              {tooltipData.countryData.countries.map((country, index) => (
                <CountryItem 
                  key={country.country}
                  flagColor={tooltipData.countryData.colors[index]}
                >
                  <div className="country-info">
                    <CountryFlag color={tooltipData.countryData.colors[index]} />
                    <span>{country.country}</span>
                  </div>
                  <div className="country-stats">
                    {country.count.toLocaleString()} ({country.percentage}%)
                  </div>
                </CountryItem>
              ))}
            </CountryList>
          </GeographicSection>
        </TooltipSection>
      )}
    </ExtendedTooltipOverlay>
  );
};

TooltipOverlay.propTypes = {
  tooltipData: PropTypes.shape({
    date: PropTypes.string.isRequired,
    sessionCount: PropTypes.number.isRequired,
    totalTimePlayed: PropTypes.number.isRequired,
    countryData: PropTypes.shape({
      chartData: PropTypes.object,
      countries: PropTypes.array,
      colors: PropTypes.array,
    })
  }),
  position: PropTypes.shape({
    top: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TooltipOverlay;
